// Verouderde tab na een deploy (bug B20, 12 okt 2026).
//
// Een deploy vervangt de code-split chunks; een tab op de oude build vraagt bij
// de volgende lazy view, modal of import() een chunk die niet meer bestaat
// (ChunkLoadError). Eén automatische reload haalt de nieuwe build op.
//
// De wacht staat in sessionStorage en is gesleuteld op de build waarop de tab
// draait (NEXT_PUBLIC_BUILD_ID, gezet in next.config.mjs uit lib/build-id.mjs):
//   - per build hooguit één reload, dus een structureel kapotte chunk lust niet
//     maar valt na de reload op de error boundary;
//   - een volgende deploy is een andere build en mag weer één keer herladen.
// Tot 12 okt 2026 bleef de oude wacht ('pb-eb-reloaded') de hele tabsessie staan,
// zodat de tweede deploy in een open tab altijd op de boundary eindigde.
//
// Eén plek voor de herkenning en de wacht: components/ErrorBoundary.js (fouten in
// render, ook van next/dynamic) en de window-luisteraars hieronder (import() in
// een handler, een losse promise) lezen allebei deze module.

export const CHUNK_RELOAD_KEY = 'pb-chunk-reload-build';

export function currentBuildId() {
  return process.env.NEXT_PUBLIC_BUILD_ID || 'dev';
}

export function isStaleChunkError(err) {
  const m = typeof err === 'string'
    ? err
    : `${err?.name || ''} ${err?.message || ''} ${err?.reason?.message || ''}`;
  return /chunkloaderror|loading (css )?chunk .*failed|loading chunk|failed to fetch dynamically imported module|importing a module script failed|error loading dynamically imported module/i.test(m);
}

// Mag deze tab nu herladen? Ja hooguit één keer per build. Zonder bruikbare
// sessionStorage nooit: liever de boundary dan een reload-lus.
export function claimChunkReload(storage, build = currentBuildId()) {
  try {
    if (!storage) return false;
    if (storage.getItem(CHUNK_RELOAD_KEY) === build) return false;
    storage.setItem(CHUNK_RELOAD_KEY, build);
    return true;
  } catch {
    return false;
  }
}

// Herlaadt bij een chunkfout als de wacht het toestaat; zegt of hij herlaadt.
export function reloadForChunkError(err, win = typeof window !== 'undefined' ? window : null) {
  if (!win || !isStaleChunkError(err)) return false;
  let storage = null;
  try { storage = win.sessionStorage; } catch { storage = null; }
  if (!claimChunkReload(storage)) return false;
  win.location.reload();
  return true;
}

// De knop 'Vernieuw pagina' op de boundary: een handmatige reload mag de wacht
// wissen, zodat de volgende chunkfout weer automatisch herstelt.
export function clearChunkReload(win = typeof window !== 'undefined' ? window : null) {
  try { win?.sessionStorage?.removeItem(CHUNK_RELOAD_KEY); } catch { /* geblokkeerd */ }
}

let _installed = false;
// Fouten buiten React-render: een import() in een klikhandler of een promise
// zonder catch. Idempotent.
export function installChunkReloadListeners(win = typeof window !== 'undefined' ? window : null) {
  if (!win || _installed) return;
  _installed = true;
  win.addEventListener('unhandledrejection', (e) => {
    if (reloadForChunkError(e.reason, win)) e.preventDefault();
  });
  win.addEventListener('error', (e) => { reloadForChunkError(e.error || e.message, win); });
}
